/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { Editor } from '@monaco-editor/react';
import { User, Bot, Clock, Copy, Check, ThumbsUp, ThumbsDown } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

import { CodeAgentChatMessage } from '@/commons/data';

interface ChatMessageProps {
    message: CodeAgentChatMessage;
    messageIndex: number;
    copiedMessageIndex: number | null;
    onCopyCode: (code: string, messageIndex: number) => void;
    onRateMessage?: (messageIndex: number, rating: 'thumbs_up' | 'thumbs_down' | null) => void;
}

export const ChatMessageComponent = ({
    message,
    messageIndex,
    copiedMessageIndex,
    onCopyCode,
    onRateMessage,
}: ChatMessageProps) => {
    const formatTimestamp = (timestamp: number) => {
        return new Date(timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const isUser = message.role === 'user';

    return (
        <div className="flex flex-col gap-2">
            {/* Header with icon and name/timestamp */}
            <div className={`flex items-center gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
                <div className="flex-shrink-0">
                    {isUser ? (
                        <div className="w-8 h-8 rounded-full bg-purple-700 flex items-center justify-center">
                            <User className="h-4 w-4 text-white" />
                        </div>
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                            <Bot className="h-4 w-4 text-purple-700" />
                        </div>
                    )}
                </div>

                <div className={`flex items-center gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
                    <div className="font-medium text-sm leading-none">{isUser ? 'You' : 'Code Agent'}</div>
                    {message.timestamp && (
                        <div className="text-xs text-muted-foreground flex items-center gap-1 leading-none">
                            <Clock className="h-3 w-3" />
                            <span>{formatTimestamp(message.timestamp)}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Message content */}
            <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                {message.role === 'assistant' ? (
                    <div className="space-y-3 w-full">
                        {/* Display content as text */}
                        {message.content && (
                            <div
                                className={
                                    'text-sm text-foreground bg-muted/30 rounded-md p-3 text-left ' +
                                    'max-w-[600px] inline-block whitespace-pre-wrap'
                                }
                            >
                                {message.content}
                            </div>
                        )}

                        {/* Display error in red */}
                        {message.error && (
                            <div
                                className={
                                    'text-sm text-red-500 bg-red-50 border border-red-200 ' +
                                    'rounded-md p-3 text-left max-w-[600px] inline-block whitespace-pre-wrap'
                                }
                            >
                                <strong>Error:</strong> {message.error}
                            </div>
                        )}

                        {/* Display code in editor */}
                        {message.code &&
                            (() => {
                                const lineCount = message.code.split('\n').length;
                                const dynamicHeight = Math.min(Math.max(lineCount * 18 + 20, 60), 400);

                                return (
                                    <div className="border rounded-md overflow-hidden min-w-0 relative w-full">
                                        <Editor
                                            height={`${dynamicHeight}px`}
                                            language="javascript"
                                            value={message.code}
                                            options={{
                                                readOnly: true,
                                                minimap: { enabled: false },
                                                scrollBeyondLastLine: false,
                                                fontSize: 12,
                                                lineNumbers: 'on',
                                                folding: false,
                                                wordWrap: 'on',
                                                scrollbar: {
                                                    vertical: 'auto',
                                                    horizontal: 'auto',
                                                },
                                                automaticLayout: true,
                                            }}
                                            theme="vs-dark"
                                        />
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <button
                                                        onClick={() => onCopyCode(message.code!, messageIndex)}
                                                        className={
                                                            'absolute bottom-2 right-2 z-10 bg-slate-700 ' +
                                                            'hover:bg-slate-600 rounded p-1.5'
                                                        }
                                                        title={
                                                            copiedMessageIndex === messageIndex
                                                                ? 'Copied!'
                                                                : 'Copy code'
                                                        }
                                                    >
                                                        {copiedMessageIndex === messageIndex ? (
                                                            <Check size={12} className="text-white" />
                                                        ) : (
                                                            <Copy size={12} className="text-white" />
                                                        )}
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>
                                                        {copiedMessageIndex === messageIndex ? 'Copied!' : 'Copy code'}
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>

                                        {/* Rating buttons */}
                                        {onRateMessage && (
                                            <div className="absolute bottom-2 right-14 z-10 flex gap-1">
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                onClick={() =>
                                                                    onRateMessage(
                                                                        messageIndex,
                                                                        message.rating === 'thumbs_up'
                                                                            ? null
                                                                            : 'thumbs_up',
                                                                    )
                                                                }
                                                                className={`rounded p-1.5 transition-colors ${
                                                                    message.rating === 'thumbs_up'
                                                                        ? 'bg-green-600 hover:bg-green-500'
                                                                        : 'bg-slate-700 hover:bg-slate-600'
                                                                }`}
                                                                title="Rate as helpful"
                                                            >
                                                                <ThumbsUp size={12} className="text-white" />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>Rate as helpful</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>

                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                onClick={() =>
                                                                    onRateMessage(
                                                                        messageIndex,
                                                                        message.rating === 'thumbs_down'
                                                                            ? null
                                                                            : 'thumbs_down',
                                                                    )
                                                                }
                                                                className={`rounded p-1.5 transition-colors ${
                                                                    message.rating === 'thumbs_down'
                                                                        ? 'bg-red-600 hover:bg-red-500'
                                                                        : 'bg-slate-700 hover:bg-slate-600'
                                                                }`}
                                                                title="Rate as not helpful"
                                                            >
                                                                <ThumbsDown size={12} className="text-white" />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>Rate as not helpful</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                    </div>
                ) : (
                    <div
                        className={
                            'text-sm text-foreground bg-primary/10 rounded-md p-3 ' +
                            'inline-block max-w-[600px] whitespace-pre-wrap'
                        }
                    >
                        {message.content}
                    </div>
                )}
            </div>
        </div>
    );
};
